import { 
  LayoutDashboard, 
  UserPlus, 
  Tag, 
  FileSpreadsheet, 
  Gift, 
  Megaphone, 
  ChartPie, 
  Building2, 
  Handshake, 
  MessageSquare 
} from 'lucide-react';

export const MODULE_CATEGORIES = {
  CORE: 'Core',
  MANAGEMENT: 'Management',
  MARKETING: 'Marketing', 
  ENGAGEMENT: 'Engagement',
  COMMUNICATION: 'Communication',
  ANALYTICS: 'Analytics',
  FINANCE: 'Finance',
  OTHER: 'Other',
};

// Backend-compatible module definitions (without "Management" suffix)
export const MODULE_DEFINITIONS = {
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Main dashboard with statistics and summary',
    category: MODULE_CATEGORIES.CORE,
    required: true,
    default: true,
  },
  campaigns: {
    id: 'campaigns',
    label: 'Campaigns',
    icon: Tag,
    description: 'Create and manage marketing campaigns',
    category: MODULE_CATEGORIES.MARKETING,
    required: false,
    default: true,
  },   
  draws: {
    id: 'draws',
    label: 'Draws',
    icon: Gift,
    description: 'Manage prize draws and giveaways',
    category: MODULE_CATEGORIES.ENGAGEMENT,
    required: false,
    default: true,
  },
  announcements: {
    id: 'announcements',
    label: 'Announcements',
    icon: Megaphone,
    description: 'Company-wide announcements and messages',
    category: MODULE_CATEGORIES.COMMUNICATION,
    required: false,
    default: true,
  }, 
  surveys: {
    id: 'surveys',
    label: 'Surveys',
    icon: FileSpreadsheet,
    description: 'Create and analyze surveys',
    category: MODULE_CATEGORIES.ENGAGEMENT,
    required: false,
    default: true,
  }, 
  brands: {
    id: 'brands',
    label: 'Brands',
    icon: Building2,
    description: 'Manage brand settings and assets',
    category: MODULE_CATEGORIES.MANAGEMENT,
    required: false,
    default: false,
  },  
  tickets: {
    id: 'tickets',
    label: 'Tickets',
    icon: MessageSquare,
    description: 'Collect and manage user tickets',
    category: MODULE_CATEGORIES.COMMUNICATION,
    required: false,
    default: false,
  },
  videos: {
    id: 'videos',
    label: 'Videos',
    icon: Handshake,
    description: 'Guide and manage videos flows',
    category: MODULE_CATEGORIES.MANAGEMENT,
    required: false,
    default: true,
  },
  users: {
    id: 'users',
    label: 'Users',
    icon: UserPlus,
    description: 'Manage company users and employees',
    category: MODULE_CATEGORIES.MANAGEMENT,
    required: false,
    default: true,
  },

  reports: {
    id: 'reports',
    label: 'Reports',
    icon: ChartPie,
    description: 'Advanced reporting and analytics',
    category: MODULE_CATEGORIES.ANALYTICS,
    required: false,
    default: false,
  },
 
  subTenants: {
    id: 'subTenants',
    label: 'subTenants',
    icon: Building2,
    description: 'Manage companies and subsidiaries',
    category: MODULE_CATEGORIES.MANAGEMENT,
    required: true,
    default: true,
  },

};

// Helper functions
export const getModulesByCategory = () => {
  const grouped = {};
  Object.values(MODULE_DEFINITIONS).forEach(module => {
    if (!grouped[module.category]) {
      grouped[module.category] = [];
    }
    grouped[module.category].push(module);
  });
  return grouped;
};

export const getModuleById = (moduleId) => {
  return MODULE_DEFINITIONS[moduleId] || null;
};

export const getEnabledModules = (enabledModuleIds) => {
  return Object.values(MODULE_DEFINITIONS).map(module => ({
    ...module,
    isEnabled: enabledModuleIds.includes(module.id),
  }));
};

export const getDefaultModules = () => {
  return Object.values(MODULE_DEFINITIONS)
    .filter(module => module.required || module.default)
    .map(module => module.id);
};

export const getRequiredModules = () => {
  return Object.values(MODULE_DEFINITIONS)
    .filter(module => module.required)
    .map(module => module.id);
};

// Category color mapping for UI
export const getCategoryColor = (category) => {
  const colorMap = {
    [MODULE_CATEGORIES.CORE]: '#3B82F6',
    [MODULE_CATEGORIES.MANAGEMENT]: '#10B981',
    [MODULE_CATEGORIES.MARKETING]: '#F59E0B',
    [MODULE_CATEGORIES.ENGAGEMENT]: '#EF4444',
    [MODULE_CATEGORIES.COMMUNICATION]: '#8B5CF6',
    [MODULE_CATEGORIES.ANALYTICS]: '#06B6D4',
    [MODULE_CATEGORIES.FINANCE]: '#84CC16',
    [MODULE_CATEGORIES.OTHER]: '#6B7280',
  };
  return colorMap[category] || colorMap[MODULE_CATEGORIES.OTHER];
};

// Validate if module exists
export const isValidModule = (moduleId) => {
  return Boolean(MODULE_DEFINITIONS[moduleId]);
};

// Get modules by status
export const getModulesByStatus = (enabledModules = []) => {
  const allModules = Object.values(MODULE_DEFINITIONS);
  const enabledSet = new Set(enabledModules);
  
  return {
    enabled: allModules.filter(module => enabledSet.has(module.id)),
    disabled: allModules.filter(module => !enabledSet.has(module.id)),
  };
};